import { defineStore, acceptHMRUpdate } from "pinia";
import { brogglBack } from "../axios.js";
import { personalReportTypes } from "../assets/variables.js";
import { useUserStore } from "./user.js";
import dayjs from "dayjs";

export const useLeadReportStore = defineStore("leadReport", {
  state: () => ({
    total: 0,
    chart: {},
    barChartKey: 0,
    empty: false,
    chartData: {
      labels: [],
      datasets: [],
    },
    parameters: {
      breakdown: "Month",
      period: "Last Quarter",
      leadReportType: personalReportTypes[0].value,
      offset: 1,
      reportType: "client",
      teamMember: {},
    },
    totals: {},
    details: {},
    favoritesPages: [],
    teamMembers: [],
    currentUserCampaigns: [],
    isExportDone: false,
  }),
  getters: {
    getTotal: (state) => state.total,
    getChart: (state) => state.chart,
    getParameters: (state) => state.parameters,
    getBarChartKey: (state) => state.barChartKey,
    getChartData: (state) => state.chartData,
    getEmpty: (state) => state.empty,
    getTotals: (state) => state.totals,
    getFavoritesPages: (state) => state.favoritesPages,
    getTeamMembers: (state) => state.teamMembers,
    getCurrentUserCampaigns: (state) => state.currentUserCampaigns,
    getIsExportDone: (state) => state.isExportDone,
  },
  actions: {
    async fetchTeamMembers() {
      let userId = useUserStore().getUserId();
      const response = await brogglBack.get("/team-members/get-members-by-user-id", {
        headers: {
          Authorization: "Bearer " + window.$cookies.get("jwt"),
        },
        params: {
          userId: userId,
        },
      });
      this.teamMembers = response.data.data;
      this.parameters.teamMember = this.teamMembers[0];
    },
    async fetchFavoritesPages() {
      const resFavorites = await brogglBack.get("/favorites", {
        headers: {
          Authorization: "Bearer " + window.$cookies.get("jwt"),
        },
        params: {
          userId: this.parameters.teamMember.UserID,
        },
      });
      for (
        let i = 1;
        i <= Object.keys(resFavorites.data.data).length / 15 + 1;
        i++
      ) {
        this.favoritesPages.push(i);
      }
    },
    async getReport() {
      this.chart = {};
      this.chartData = {
        labels: [],
        datasets: [],
      };
      this.total = 0;
      this.empty = false;
      let userId = useUserStore().getUserId();
      if (this.parameters.leadReportType === "project") {
        const res = await brogglBack.get("/report/user/lead-report", {
          headers: {
            Authorization: "Bearer " + window.$cookies.get("jwt"),
          },
          params: {
            period: this.parameters.period,
            breakdown: this.parameters.breakdown,
            userId: userId,
            offset: this.parameters.offset,
            reportType: this.parameters.leadReportType,
            teamMemberId: this.parameters.teamMember.userId,
          },
        });
        let sumCS = [];
        let sumPS = [];
        let sumOnBoarding = [];
        let sumOtherProjects = [];
        for (let i = 0; i < res.data.data["report"].length; i++) {
          this.total += res.data.data["report"][i].sumAllProjects;
          sumCS.push(res.data.data["report"][i].sumCS);
          sumPS.push(res.data.data["report"][i].sumPS);
          sumOnBoarding.push(res.data.data["report"][i].sumOnBoarding);
          sumOtherProjects.push(res.data.data["report"][i].sumOtherProjects);
        }
        this.chartData.datasets.push({
          label: "CS",
          backgroundColor: "#217981",
          borderColor: "#000000",
          data: sumCS,
        });
        this.chartData.datasets.push({
          label: "PS",
          backgroundColor: "#4ca2cd",
          borderColor: "#000000",
          data: sumPS,
        });
        this.chartData.datasets.push({
          label: "OnBoarding",
          backgroundColor: "#4ddbb8",
          borderColor: "#000000",
          data: sumOnBoarding,
        });
        this.chartData.datasets.push({
          label: "Other Projects",
          backgroundColor: "#48d4d4",
          borderColor: "#000000",
          data: sumOtherProjects,
        });

        this.chartData.labels = res.data.data["report"].map((item) =>
          dayjs(item.breakdown).format("MMM-DD-YYYY"),
        );
        this.chart = res.data.data["report"];
        this.currentUserCampaigns = res.data.data["campaignForUser"];
      } else if (this.parameters.leadReportType === "billable") {
        const res = await brogglBack.get("/report/user/lead-report", {
          headers: {
            Authorization: "Bearer " + window.$cookies.get("jwt"),
          },
          params: {
            period: this.parameters.period,
            breakdown: this.parameters.breakdown,
            userId: userId,
            offset: this.parameters.offset,
            reportType: this.parameters.leadReportType,
            teamMemberId: this.parameters.teamMember.userId,
          },
        });
        let sumBillable = [];
        let sumNonBillable = [];
        let sumInternal = [];
        for (let i = 0; i < res.data.data["report"].length; i++) {
          this.total += res.data.data["report"][i].sumAll;
          sumBillable.push(res.data.data["report"][i].sumBillable);
          sumNonBillable.push(res.data.data["report"][i].sumNonBillable);
          sumInternal.push(res.data.data["report"][i].sumInternal);
        }
        this.chartData.datasets.push({
          label: "Billable",
          backgroundColor: "#217981",
          borderColor: "#000000",
          fill: false,
          data: sumBillable,
        });
        this.chartData.datasets.push({
          label: "Non Billable",
          backgroundColor: "#4ca2cd",
          borderColor: "#000000",
          fill: false,
          data: sumNonBillable,
        });
        this.chartData.datasets.push({
          label: "Internal",
          backgroundColor: "#4ddbb8",
          borderColor: "#000000",
          fill: false,
          data: sumInternal,
        });
        this.chartData.labels = res.data.data["report"].map((item) =>
          dayjs(item.breakdown).format("MMM-DD-YYYY"),
        );
        this.chart = res.data.data["report"];
        this.currentUserCampaigns = res.data.data.campaignForUser;
      } else {
        this.favoritesPages = [];
        await this.fetchFavoritesPages();
        if (this.favoritesPages.length > 0) {
          const res = await brogglBack.get("/report/user/lead-report", {
            headers: {
              Authorization: "Bearer " + window.$cookies.get("jwt"),
            },
            params: {
              period: this.parameters.period,
              breakdown: this.parameters.breakdown,
              userId: userId,
              offset: this.parameters.offset,
              reportType: this.parameters.leadReportType,
              teamMemberId: this.parameters.teamMember.userId,
            },
          });
          if (res.data.data !== null) {
            var dataset = [];
            res.data.data["report"].forEach((item) => {
              this.total += item.sum;
              dataset.push(item.sum);
            });
            this.chartData.datasets.push({
              label: "Client",
              backgroundColor: "#217981",
              borderColor: "#000000",
              fill: false,
              data: dataset,
            });
            this.chartData.labels = res.data.data["report"].map(
              (item) => item.name,
            );
            this.chart = res.data.data["report"];
            this.currentUserCampaigns = res.data.data["campaignForUser"];
          }
        }
      }
      this.barChartKey++;
      this.empty = this.chartData.datasets.every(
        (dataset) => dataset.data.length === 0,
      );
      this.total = this.total.toLocaleString();
    },
    calculateTotals() {
      this.totals = {};
      if (this.parameters.leadReportType === "project") {
        this.totals.CS = this.chart.reduce((acc, curr) => acc + curr.sumCS, 0);
        this.totals.PS = this.chart.reduce((acc, curr) => acc + curr.sumPS, 0);
        this.totals.OnBoarding = this.chart.reduce(
          (acc, curr) => acc + curr.sumOnBoarding,
          0,
        );
        this.totals.OtherProjects = this.chart.reduce(
          (acc, curr) => acc + curr.sumOtherProjects,
          0,
        );
      } else if (this.parameters.leadReportType === "billable") {
        this.totals.Billable = this.chart.reduce(
          (acc, curr) => acc + curr.sumBillable,
          0,
        );
        this.totals.NonBillable = this.chart.reduce(
          (acc, curr) => acc + curr.sumNonBillable,
          0,
        );
        this.totals.Internal = this.chart.reduce(
          (acc, curr) => acc + curr.sumInternal,
          0,
        );
      }
      Object.keys(this.totals).forEach((total) => {
        total = total.toLocaleString();
      });
    },
    convertArrayOfObjectsToCSV(args) {
      var result, columnDelimiter, lineDelimiter, data;
      data = args.data;
      columnDelimiter = ",";
      lineDelimiter = "\n";
      result = "";
      let keys = Object.keys(data[0]);
      result += keys.join(columnDelimiter) + lineDelimiter;
      data.forEach(function (item) {
        Object.keys(item).forEach((key) => {
          result += item[key] + columnDelimiter;
        });
        result += lineDelimiter;
      });
      return result;
    },
    csvExport(arrData) {
      var data, filename, link;
      var csv = this.convertArrayOfObjectsToCSV({
        data: arrData,
      });
      if (csv == null) return;
      filename = arrData.filename || "export.csv";
      if (!csv.match(/^data:text\/csv/i)) {
        csv = "data:text/csv;charset=utf-8," + csv;
      }
      data = encodeURI(csv);
      link = document.createElement("a");
      link.setAttribute("href", data);
      link.setAttribute("download", filename);
      link.click();
    },
    async getExportForAllTeamMember() {
      this.isExportDone = true;
      try {
        const res = await brogglBack.get(
          "/report/user/all-team-members-campaigns",
          {
            headers: {
              Authorization: "Bearer " + window.$cookies.get("jwt"),
            },
            params: {
              period: this.parameters.period,
              breakdown: this.parameters.breakdown,
              userId: useUserStore().getUserId(),
            },
          },
        );
        var result, columnDelimiter, lineDelimiter;
        columnDelimiter = ",";
        lineDelimiter = "\n";
        result = "";
        result +=
          "User Name, Campaign,Client Id,Client Name,Man Days Used,Complexity,Time Spent On Campaign,Date";
        result += lineDelimiter;
        res.data.data.forEach(function (item) {
          result += item.userName + columnDelimiter;
          result += item.id + columnDelimiter;
          result += item.clientId + columnDelimiter;
          result += item.clientName + columnDelimiter;
          result += item.manDaysUsed + columnDelimiter;
          result += item.complexityOfTest + columnDelimiter;
          result += item.timeSpentOnCampaign + columnDelimiter;
          result += item.date;
          result += lineDelimiter;
        });
        var csv = result;
        let today = new Date();
        var filename = "export_all_campaigns_" + today + ".csv";
        filename = filename.replace(/\s/g, "_").replace(/[^a-zA-Z0-9_]/g, "");
        filename = filename || "export.csv";
        if (!csv.match(/^data:text\/csv/i)) {
          csv = "data:text/csv;charset=utf-8," + csv;
        }
        let data = encodeURI(csv);
        let link = document.createElement("a");
        link.setAttribute("href", data);
        link.setAttribute("download", filename);
        link.click();
        this.isExportDone = false;
      } catch (error) {
        console.log(error);
      }
    },
    async getReportForOtherRole(teamLeaderId) {
      this.chart = {};
      this.chartData = {
        labels: [],
        datasets: [],
      };
      this.total = 0;
      this.empty = false;
      if (this.parameters.leadReportType === "project") {
        console.log("this.parameters.teamMember: ", this.parameters.teamMember);
        console.log("this.parameters.teamMember.UserID: ", this.parameters.teamMember.UserID);
        const res = await brogglBack.get("/report/user/lead-report", {
          headers: {
            Authorization: "Bearer " + window.$cookies.get("jwt"),
          },
          params: {
            period: this.parameters.period,
            breakdown: this.parameters.breakdown,
            userId: teamLeaderId,
            offset: this.parameters.offset,
            reportType: this.parameters.leadReportType,
            teamMemberId: this.parameters.teamMember.UserID,
          },
        });
        let sumCS = [];
        let sumPS = [];
        let sumOnBoarding = [];
        let sumOtherProjects = [];
        for (let i = 0; i < res.data.data["report"].length; i++) {
          this.total += res.data.data["report"][i].sumAllProjects;
          sumCS.push(res.data.data["report"][i].sumCS);
          sumPS.push(res.data.data["report"][i].sumPS);
          sumOnBoarding.push(res.data.data["report"][i].sumOnBoarding);
          sumOtherProjects.push(res.data.data["report"][i].sumOtherProjects);
        }
        this.chartData.datasets.push({
          label: "CS",
          backgroundColor: "#217981",
          borderColor: "#000000",
          data: sumCS,
        });
        this.chartData.datasets.push({
          label: "PS",
          backgroundColor: "#4ca2cd",
          borderColor: "#000000",
          data: sumPS,
        });
        this.chartData.datasets.push({
          label: "OnBoarding",
          backgroundColor: "#4ddbb8",
          borderColor: "#000000",
          data: sumOnBoarding,
        });
        this.chartData.datasets.push({
          label: "Other Projects",
          backgroundColor: "#48d4d4",
          borderColor: "#000000",
          data: sumOtherProjects,
        });

        this.chartData.labels = res.data.data["report"].map((item) =>
          dayjs(item.breakdown).format("MMM-DD-YYYY"),
        );
        this.chart = res.data.data["report"];
        this.currentUserCampaigns = res.data.data["campaignForUser"];
      } else if (this.parameters.leadReportType === "billable") {
        const res = await brogglBack.get("/report/user/lead-report", {
          headers: {
            Authorization: "Bearer " + window.$cookies.get("jwt"),
          },
          params: {
            period: this.parameters.period,
            breakdown: this.parameters.breakdown,
            userId: teamLeaderId,
            offset: this.parameters.offset,
            reportType: this.parameters.leadReportType,
            teamMemberId: this.parameters.teamMember.UserID,
          },
        });
        let sumBillable = [];
        let sumNonBillable = [];
        let sumInternal = [];
        for (let i = 0; i < res.data.data["report"].length; i++) {
          this.total += res.data.data["report"][i].sumAll;
          sumBillable.push(res.data.data["report"][i].sumBillable);
          sumNonBillable.push(res.data.data["report"][i].sumNonBillable);
          sumInternal.push(res.data.data["report"][i].sumInternal);
        }
        this.chartData.datasets.push({
          label: "Billable",
          backgroundColor: "#217981",
          borderColor: "#000000",
          fill: false,
          data: sumBillable,
        });
        this.chartData.datasets.push({
          label: "Non Billable",
          backgroundColor: "#4ca2cd",
          borderColor: "#000000",
          fill: false,
          data: sumNonBillable,
        });
        this.chartData.datasets.push({
          label: "Internal",
          backgroundColor: "#4ddbb8",
          borderColor: "#000000",
          fill: false,
          data: sumInternal,
        });
        this.chartData.labels = res.data.data["report"].map((item) =>
          dayjs(item.breakdown).format("MMM-DD-YYYY"),
        );
        this.chart = res.data.data["report"];
        this.currentUserCampaigns = res.data.data["campaignForUser"];
      } else {
        this.favoritesPages = [];
        await this.fetchFavoritesPages();
        if (this.favoritesPages.length > 0) {
          const res = await brogglBack.get("/report/user/lead-report", {
            headers: {
              Authorization: "Bearer " + window.$cookies.get("jwt"),
            },
            params: {
              period: this.parameters.period,
              breakdown: this.parameters.breakdown,
              userId: teamLeaderId,
              offset: this.parameters.offset,
              reportType: this.parameters.leadReportType,
              teamMemberId: this.parameters.teamMember.UserID,
            },
          });
          if (res.data.data !== null) {
            var dataset = [];
            console.log(res.data)
            res.data.data["report"].forEach((item) => {
              this.total += item.sum;
              dataset.push(item.sum);
            });
            this.chartData.datasets.push({
              label: "Client",
              backgroundColor: "#217981",
              borderColor: "#000000",
              fill: false,
              data: dataset,
            });
            this.chartData.labels = res.data.data["report"].map(
              (item) => item.name,
            );
            this.chart = res.data.data["report"];
            this.currentUserCampaigns = res.data.data["campaignForUser"];
          }
        }
      }
      this.barChartKey++;
      this.empty = this.chartData.datasets.every(
        (dataset) => dataset.data.length === 0,
      );
      this.total = this.total.toLocaleString();
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useLeadReportStore, import.meta.hot));
}
